// LF360-1830 — the UI shows a task's reminders as ONE list; storage still keeps
// the first one in the activity's own columns (remind / remindHour / remindMinute,
// or triggerOffsetMinutes when relative) and the rest in `extraReminders`.
//
// That split is a storage detail, not a concept the admin should have to think
// about: a task simply has zero, one or many reminders. These helpers translate
// between the two, and are kept pure so the rules can be tested directly.

export const MAX_REMINDERS_PER_TASK = 10;
export const MESSAGE_KINDS = { TASK_DUE: "task_due", TASK_UPCOMING: "task_upcoming" };

// Identifies the reminder held in the activity's own columns. The notification
// ledger uses the same sentinel for its rows.
export const BASE_REMINDER_ID = "base";

// The backend accepts a relative offset within a day in either direction.
const MAX_OFFSET_MINUTES = 1439;

// The backend caps a lead time at 365 days.
const MAX_LEAD_DAYS = 365;

// Exported so a caller building a reminder itself does not have to repeat this
// fallback. Nothing about the existing helpers changes.
export const newReminderId = () =>
  window.crypto && window.crypto.randomUUID
    ? window.crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(16).slice(2)}`;

const uuid = newReminderId;

const isLeadTime = (reminder) => reminder?.messageKind === MESSAGE_KINDS.TASK_UPCOMING;

const minutesOf = (reminder) =>
  reminder?.notificationType === "relative"
    ? Number(reminder.triggerOffsetMinutes) || 0
    : (Number(reminder?.remindHour) || 0) * 60 + (Number(reminder?.remindMinute) || 0);

/**
 * Read an activity (or subject task) into the single list the UI edits.
 *
 * Ordered earliest first, with pre-assignment reminders last: those fire before
 * the task exists, so they do not belong on the same timeline as the rest.
 */
export const remindersFromActivity = (activity = {}) => {
  const notificationType = activity.notificationType === "relative" ? "relative" : "absolute";
  const list = [];

  if (notificationType === "relative" && activity.remind !== false) {
    list.push({
      id: BASE_REMINDER_ID,
      notificationType: "relative",
      triggerOffsetMinutes: Number(activity.triggerOffsetMinutes) || 0,
      triggerSubjectProfileFieldId: activity.triggerSubjectProfileFieldId || null,
      messageKind: MESSAGE_KINDS.TASK_DUE,
    });
  } else if (activity.remind && Number.isInteger(activity.remindHour)) {
    list.push({
      id: BASE_REMINDER_ID,
      notificationType: "absolute",
      remindHour: activity.remindHour,
      remindMinute: Number.isInteger(activity.remindMinute) ? activity.remindMinute : 0,
      messageKind: MESSAGE_KINDS.TASK_DUE,
    });
  }

  if (Array.isArray(activity.extraReminders)) {
    // LF360-1881: a reminder counts from the profile field it names. One saved
    // before that was true names none, and has been counting from the activity's
    // — so it is shown that way, and saving writes it in for good.
    list.push(
      ...activity.extraReminders.filter(Boolean).map((reminder) =>
        reminder.notificationType === "relative" && !reminder.triggerSubjectProfileFieldId
          ? {
              ...reminder,
              triggerSubjectProfileFieldId: activity.triggerSubjectProfileFieldId || null,
            }
          : reminder
      )
    );
  }

  return sortReminders(list);
};

// Fixed times first, then offsets, then the ones that fire before the task is
// assigned. Comparing an offset against a time of day would be comparing "two
// hours after waking" with "09:00", which has no answer — and the order decides
// which reminder is kept in the activity's own columns, so it cannot be arbitrary
// (LF360-1881). A list of one kind sorts exactly as it always did.
const kindOrder = (reminder) => {
  if (isLeadTime(reminder)) return 2;

  return reminder?.notificationType === "relative" ? 1 : 0;
};

export const sortReminders = (list) =>
  [...list].sort((a, b) => {
    if (kindOrder(a) !== kindOrder(b)) return kindOrder(a) - kindOrder(b);
    // Among pre-assignment reminders, the furthest ahead of the assignment first.
    if (isLeadTime(a) && isLeadTime(b)) {
      const days = (r) => (r.unit === "hours" ? Number(r.value) / 24 : Number(r.value)) || 0;
      return days(b) - days(a);
    }
    return minutesOf(a) - minutesOf(b);
  });

/**
 * Split the list back into the shape the API expects.
 *
 * The earliest ordinary reminder becomes the activity's own; everything else goes
 * to `extraReminders`. A pre-assignment reminder can never be the activity's own
 * one — it has no time on the assignment day — so it always stays an extra.
 */
export const remindersToPayload = (list = [], notificationTypeHint = "absolute") => {
  const ordered = sortReminders(list.filter(Boolean));
  const ordinary = ordered.filter((r) => !isLeadTime(r));
  const leadTime = ordered.filter(isLeadTime);
  const [first, ...rest] = ordinary;

  // A list may now hold both kinds, so what ends up in the activity's own columns
  // decides its type — reading it off the caller's hint would store a relative
  // first reminder as midnight (LF360-1881). The hint still answers for an empty
  // list, where there is no reminder to ask.
  const notificationType = first
    ? (first.notificationType === "relative" ? "relative" : "absolute")
    : notificationTypeHint;

  // Strip the sentinel id from whatever became the activity's own reminder, and
  // give any promoted reminder a real uuid: the ledger keys its rows by this id.
  const asExtra = (reminder) => ({
    ...reminder,
    id: reminder.id === BASE_REMINDER_ID ? uuid() : reminder.id,
  });
  const extraReminders = [...rest.map(asExtra), ...leadTime.map(asExtra)];

  if (notificationType === "relative") {
    return {
      notificationType: "relative",
      remind: Boolean(first),
      remindHour: null,
      remindMinute: null,
      triggerOffsetMinutes: first ? Number(first.triggerOffsetMinutes) || 0 : 0,
      // The field belongs to the reminder, and this one's has to travel with it
      // into the columns it is stored in.
      triggerSubjectProfileFieldId: (first && first.triggerSubjectProfileFieldId) || null,
      extraReminders,
    };
  }

  if (!first) {
    // No reminders at all is a legitimate state, and the only way to express it
    // is the explicit off-switch — an absent time means "keep" server-side.
    return {
      notificationType: "absolute",
      remind: false,
      remindHour: null,
      remindMinute: null,
      triggerOffsetMinutes: 0,
      extraReminders,
    };
  }

  return {
    notificationType: "absolute",
    remind: true,
    remindHour: Number(first.remindHour) || 0,
    remindMinute: Number(first.remindMinute) || 0,
    triggerOffsetMinutes: 0,
    extraReminders,
  };
};

const normalizedType = (type) => (type === "relative" ? "relative" : "absolute");

/**
 * Keep unsaved reminder lists separate while an editor is open.
 *
 * The store is deliberately owned by the mounted screen (normally a useRef(Map)).
 * It therefore survives React/API refreshes on that screen, but disappears on a
 * browser reload, where persisted activity data becomes the only source again.
 */
export const rememberReminderTypeDraft = (store, key, type, reminders = []) => {
  if (!(store instanceof Map) || !key) return;
  const bucket = store.get(key) || {};
  bucket[normalizedType(type)] = Array.isArray(reminders) ? [...reminders] : [];
  store.set(key, bucket);
};

export const switchReminderTypeDraft = (
  store,
  key,
  currentType,
  currentReminders,
  nextType
) => {
  rememberReminderTypeDraft(store, key, currentType, currentReminders);
  const bucket = store instanceof Map ? store.get(key) || {} : {};
  const target = bucket[normalizedType(nextType)];
  return Array.isArray(target) ? [...target] : [];
};

/**
 * A reminder to append, at a time that is not taken yet.
 *
 * An hour after the latest one, per the ticket's review: two reminders at the
 * same time are refused by the backend, and the admin should not have to discover
 * that by hitting the error.
 */
export const nextReminder = (
  list = [],
  notificationType = "absolute",
  // Which profile time a new offset reminder starts out counting from. The
  // backend refuses one that names none, so a row is never added without it
  // (LF360-1881).
  { triggerSubjectProfileFieldId = null } = {}
) => {
  const ordinary = list.filter((r) => r && !isLeadTime(r));

  if (notificationType === "relative") {
    const taken = new Set(ordinary.map(minutesOf));
    const latest = ordinary.length
      ? Math.max(...ordinary.map(minutesOf))
      : null;

    // An hour later when that is free, otherwise the nearest free offset in the
    // signed range. Clamping to the bound instead produced the offset that was
    // already there, and the backend rejected the pair as duplicates.
    let offset = latest === null ? 0 : latest + 60;
    if (offset > MAX_OFFSET_MINUTES || taken.has(offset)) {
      offset = null;
      for (let step = 15; step <= MAX_OFFSET_MINUTES; step += 15) {
        if (!taken.has(step)) { offset = step; break; }
        if (!taken.has(-step)) { offset = -step; break; }
      }
      if (offset === null && !taken.has(0)) { offset = 0; }
    }
    if (offset === null) return null;

    return {
      id: uuid(),
      notificationType: "relative",
      triggerOffsetMinutes: offset,
      triggerSubjectProfileFieldId,
      messageKind: MESSAGE_KINDS.TASK_DUE,
    };
  }

  const taken = new Set(ordinary.map(minutesOf));
  let minutes = ordinary.length === 0 ? 9 * 60 : Math.max(...ordinary.map(minutesOf)) + 60;

  // Past the end of the day, fall back to the first free slot so "Add" always
  // produces something valid rather than a duplicate.
  if (minutes > 23 * 60 + 59 || taken.has(minutes)) {
    minutes = -1;
    for (let candidate = 0; candidate <= 23 * 60 + 59; candidate += 30) {
      if (!taken.has(candidate)) {
        minutes = candidate;
        break;
      }
    }
  }
  if (minutes < 0) return null; // every half-hour slot taken — nothing sensible to add

  return {
    id: uuid(),
    notificationType: "absolute",
    remindHour: Math.floor(minutes / 60),
    remindMinute: minutes % 60,
    messageKind: MESSAGE_KINDS.TASK_DUE,
  };
};

// The relative offset is edited as a sign plus hours and minutes, the way it has
// always been shown, while storage keeps a single signed number of minutes.
export const minutesToOffsetParts = (totalMinutes = 0) => {
  const numeric = Number(totalMinutes || 0);
  const abs = Math.abs(numeric);

  return {
    sign: numeric < 0 ? "-" : "+",
    hours: Math.floor(abs / 60),
    minutes: abs % 60,
  };
};

export const offsetPartsToMinutes = (sign, hours, minutes) => {
  const safeHours = Math.max(0, Math.min(23, Number(hours || 0)));
  const safeMinutes = Math.max(0, Math.min(59, Number(minutes || 0)));
  const total = safeHours * 60 + safeMinutes;

  return sign === "-" ? -total : total;
};

/**
 * A pre-assignment reminder ("3 days before"), only valid for triggered tasks.
 *
 * A day further ahead than the furthest existing one: the backend compares whole
 * reminders, so adding twice used to produce two identical "1 day before" entries
 * and be rejected as duplicates.
 */
export const nextLeadTimeReminder = (list = []) => {
  const existing = (Array.isArray(list) ? list : []).filter(isLeadTime);
  const takenDays = new Set(
    existing
      .filter((r) => (r.unit || "days") === "days")
      .map((r) => Number(r.value) || 0)
  );

  let value = existing.length
    ? Math.max(...existing.map((r) => ((r.unit || "days") === "days" ? Number(r.value) || 0 : 0))) + 1
    : 1;

  if (value > MAX_LEAD_DAYS || takenDays.has(value)) {
    value = null;
    for (let candidate = 1; candidate <= MAX_LEAD_DAYS; candidate += 1) {
      if (!takenDays.has(candidate)) { value = candidate; break; }
    }
  }
  if (value === null) return null;

  return {
    id: uuid(),
    notificationType: "absolute",
    value,
    unit: "days",
    remindHour: 9,
    remindMinute: 0,
    messageKind: MESSAGE_KINDS.TASK_UPCOMING,
  };
};


/**
 * LF360-1940 — the reminders a task's defaults give to something that cannot
 * send all of them.
 *
 * Only a triggered activity knows its date far enough ahead to announce itself
 * before it exists, so everywhere else an announcement is dropped rather than
 * shown. The backend does the same on the way in; showing one here and removing
 * it on save is the worst of the two, because the admin reads it as saved.
 *
 * One place, because the two drawers on a subject's plan both need it — the one
 * that assigns a task and the one that resets an assignment back to it — and a
 * filter written twice is a filter that will be right once.
 */
export const remindersForPersonalTask = (defaults) =>
  remindersFromActivity(defaults || {}).filter((reminder) => !isLeadTime(reminder));
